import { UUID } from "io-ts-types/lib/UUID"
import { Dispatch, SetStateAction, useEffect } from "react"
import { useLocalStorage } from "react-use"

const SYNC_INTERNAL_FLAGS_EVENT_TYPE = `SYNC_INTERNAL_FLAGS`

const getLocalStorageKey = (userId?: UUID): string => `__flock_internal_flags__.${userId ?? `default`}`

export type InternalFlags = Readonly<{
  isDemoModeEnabled: boolean
}>

export const DEFAULT_INTERNAL_FLAGS: InternalFlags = {
  isDemoModeEnabled: false,
}

type UseInternalFlagsReturn = Readonly<{
  flags: InternalFlags
  setFlags: Dispatch<SetStateAction<InternalFlags | undefined>>
}>

export const useInternalFlags = (userId?: UUID): UseInternalFlagsReturn => {
  const [flags, setFlags] = useLocalStorage<InternalFlags>(getLocalStorageKey(userId), DEFAULT_INTERNAL_FLAGS)

  // Sync internal flags across iframes and tabs/windows
  useEffect(() => {
    const onStorageChange = (event: StorageEvent): void => {
      if (userId && event.key === getLocalStorageKey(userId) && event.newValue) {
        setFlags(JSON.parse(event.newValue))
      }
    }

    window.addEventListener(`storage`, onStorageChange)

    return () => {
      window.removeEventListener(`storage`, onStorageChange)
    }
  }, [setFlags, userId])

  // Listen for messages from iframes and respond with the internal flags
  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      if (event.data.type === SYNC_INTERNAL_FLAGS_EVENT_TYPE && event.data.key) {
        event.source?.postMessage(
          {
            type: event.data.type,
            key: event.data.key,
            flags: localStorage.getItem(event.data.key) ?? JSON.stringify(DEFAULT_INTERNAL_FLAGS),
          },
          // @ts-ignore - this works, regardless of the TS types.
          event.origin
        )
      }
    }

    window.addEventListener(`message`, onMessage)

    return () => {
      window.removeEventListener(`message`, onMessage)
    }
  }, [])

  return {
    flags: flags ?? DEFAULT_INTERNAL_FLAGS,
    setFlags,
  }
}
